import { ActionCreatorsMapObject } from 'redux';
import { ItemIDs } from '../../Config/Items';
import Core from '../../Core/Core';
import { IFBFriend } from '../../SDK/SDKfb';
import { Collection } from '../../Tiles/TileSet';
import { BoosterId } from '../reducers/boosters';
import { IStuckArgs } from '../reducers/level';
import { IDailyMission } from '../reducers/missions';
import { ITutorialUpdatePayload } from '../reducers/tutorial';
import Purchase = FBInstant.Purchase;

type ActionsUnion<A extends ActionCreatorsMapObject> = ReturnType<A[keyof A]>;

export type TAction<T extends string = string, P = void> = P extends void
    ? Readonly<{ type: T }>
    : Readonly<{ type: T; payload: P }>;

export function createAction<T extends string>(type: T) {
    function actionPayload(): () => TAction<T>;
    function actionPayload<P>(): (payload: P) => TAction<T, P>;
    function actionPayload<P>() {
        return (payload?: P) => ({ type, payload });
    }

    return actionPayload;
}

export interface IMapDataPayload {
    id: string;
    stars: number;
}

export const Actions = {
    activateBooster: (id: BoosterId) => createAction(ActionTypes.ACTIVATE_BOOSTER)<BoosterId>()(id),
    activateFriendsBoosters: (animate?: boolean) =>
        createAction(ActionTypes.ACTIVATE_FRIENDS_BOOSTERS)<boolean>()(animate),
    askFriend: (friend: IFBFriend) =>
        createAction(ActionTypes.ASK_FRIEND_START)<IFBFriend>()(friend),
    askFriendComplete: () => createAction(ActionTypes.ASK_FRIEND_COMPLETE)()(),
    addFriendBooster: (id: BoosterId) =>
        createAction(ActionTypes.ADD_FRIEND_BOOSTER)<BoosterId>()(id),
    addBoosters: (ids: BoosterId[]) =>
        createAction(ActionTypes.ADD_GAME_BOOSTER)<BoosterId[]>()(ids),
    addFreeBooster: (id: ItemIDs) => createAction(ActionTypes.ADD_FREE_BOOSTER)<ItemIDs>()(id),
    addGiftToCounter: () => createAction(ActionTypes.ADD_GIFT_TO_COUNTER)()(),
    addScore: (s: number) => {
        if (Core.store.getState().StateLevel.timed) {
            return Actions.completeAction();
        }
        return createAction(ActionTypes.ADD_LEVEL_SCORE)<number>()(s);
    },
    setScore: (score: number) => createAction(ActionTypes.SET_LEVEL_SCORE)<number>()(score),
    addInvitation: (id: string) => createAction(ActionTypes.ADD_INVITATION)<string>()(id),
    addChest: () => createAction(ActionTypes.ADD_CHEST)()(),
    boosterActionComplete: () => {
        return createAction(ActionTypes.BOOSTER_ACTION_COMPLETE)<boolean>()(
            Core.store.getState().StateLevel.stuck
        );
    },
    blockBoard: (b: boolean) => createAction(ActionTypes.BLOCK_BOARD)<boolean>()(b),
    boardReady: (b: boolean) => createAction(ActionTypes.BOARD_READY)<boolean>()(b),
    cancelBooster: (id: BoosterId) => createAction(ActionTypes.CANCEL_BOOSTER)<BoosterId>()(id),
    changeChestState: (args: [number, string]) =>
        createAction(ActionTypes.CHANGE_CHEST_STATE)<[number, string]>()(args),
    clearPreLevel: () => createAction(ActionTypes.CLEAR_PRELEVEL)()(),
    changeMap: (id: string) => createAction(ActionTypes.CHANGE_MAP)<string>()(id),
    changeView: (id: string) => createAction(ActionTypes.CHANGE_VIEW)<string>()(id),
    completeAction: () => createAction(ActionTypes.ACTION_COMPLETE)()(),
    countForMission: (args: [string, number]) =>
        createAction(ActionTypes.MISSION_COUNT)<[string, number]>()(args),
    countObject: (ids: string[]) => createAction(ActionTypes.COUNT_OBJECTS)<string[]>()(ids),
    findObject: (ids: string[]) => createAction(ActionTypes.FIND_OBJECT)<string[]>()(ids),
    gameTracking: (id: string) => createAction(ActionTypes.GAME_TRACKING)<string>()(id),
    inviteConfirm: (ids: string[]) => createAction(ActionTypes.INVITE_CONFIRMED)<string[]>()(ids),
    inviteDelete: () => createAction(ActionTypes.DELETE_INVITE_CONFIRMED)()(),
    incrementBoosterMatch: () => createAction(ActionTypes.INCREMENT_BOOSTER_MATCH)()(),
    initCore: () => createAction(ActionTypes.INIT_CORE)()(),
    initLevel: (id: string) => createAction(ActionTypes.INIT_LEVEL)<string>()(id),
    interstitialAdWatched: () => createAction(ActionTypes.INTERSTITIAL_AD_WATCHED)()(),
    levelComplete: () => createAction(ActionTypes.LEVEL_COMPLETE)()(),
    completeLevel: () => createAction(ActionTypes.COMPLETE_LEVEL)()(),
    setLevelStuck: ({ reason, stuck = true }: IStuckArgs) => {
        const state = Core.store.getState().StateLevel;
        if (!state.complete && state.stuck && !stuck && reason !== state.stuckReason) {
            stuck = state.stuck;
            reason = state.stuckReason;
        }
        return createAction(ActionTypes.LEVEL_STUCK)<IStuckArgs>()({ reason, stuck });
    },
    setWinTime: (time: number) => createAction(ActionTypes.SET_WIN_TIME)<number>()(time),
    addWinTime: (time: number) => createAction(ActionTypes.ADD_WIN_TIME)<number>()(time),
    loadLevel: (id: string) => createAction(ActionTypes.LOAD_LEVEL)<string>()(id),
    matchTiles: (vals: string[]) => createAction(ActionTypes.MATCH_TILES)<string[]>()(vals),
    missionComplete: (id: string) => createAction(ActionTypes.MISSION_COMPLETE)<string>()(id),
    missionInit: (args: [IDailyMission[], number, boolean]) =>
        createAction(ActionTypes.MISSION_INIT)<[IDailyMission[], number, boolean?]>()(args),
    missionReplace: (id: string) => {
        const playerIndex = Core.store.getState().StatePlayer.lastLevelIndex;
        return createAction(ActionTypes.MISSION_REPLACE)<Array<string | number>>()([
            id,
            playerIndex,
        ]);
    },
    missionRefresh: (id: string) => createAction(ActionTypes.MISSION_REFRESH)<string>()(id),
    missionRefreshComplete: () => {
        const playerIndex = Core.store.getState().StatePlayer.lastLevelIndex;
        return createAction(ActionTypes.MISSION_REFRESH_COMPLETE)<number>()(playerIndex);
    },
    missionMilestone: (n: number) =>
        createAction(ActionTypes.MISSION_MILESTONE_REACHED)<number>()(n),
    missionsShown: () => createAction(ActionTypes.MISSIONS_SHOWN)()(),
    optInAccepted: () => createAction(ActionTypes.OPT_IN_ACCEPTED)()(),
    optInDismissed: () => createAction(ActionTypes.OPT_IN_DISMISSED)()(),
    pauseMenuVisible: (visible: boolean) => {
        const isVisible = Core.store.getState().StateLevel.stuck || visible;
        return createAction(ActionTypes.PAUSE_MENU_VISIBLE)<boolean>()(isVisible);
    },
    shortcutAccepted: () => createAction(ActionTypes.OPT_IN_SHORTCUT_ACCEPTED)()(),
    shortcutDismissed: () => createAction(ActionTypes.OPT_IN_SHORTCUT_DISMISSED)()(),
    shortcutRewarded: () => createAction(ActionTypes.OPT_IN_SHORTCUT_REWARDED)()(),
    optInRewarded: () => createAction(ActionTypes.OPT_IN_REWARDED)()(),
    optInPrompt: () => createAction(ActionTypes.OPT_IN_PROMT)()(),
    nativeOptInPrompt: () => createAction(ActionTypes.NATIVE_OPT_IN_PROMT)()(),
    nativeOptInShortcutPrompt: () => createAction(ActionTypes.NATIVE_OPT_IN_SHORTCUT_PROMT)()(),
    optInShown: () => createAction(ActionTypes.OPT_IN_SHOWN)()(),
    optInShortcutShown: () => createAction(ActionTypes.OPT_IN_SHORTCUT_SHOWN)()(),
    savePurchase: (purchase: Purchase) =>
        createAction(ActionTypes.SAVE_PURCHASE)<Purchase>()(purchase),
    popupShown: () => createAction(ActionTypes.POPUP_SHOWN)()(),
    popupDismissed: () => {
        return createAction(ActionTypes.POPUP_DISMISSED)<boolean>()(
            Core.store.getState().StateLevel.stuck
        );
    },
    receiveGift: (args: [string, number, string?]) =>
        createAction(ActionTypes.RECEIVE_GIFT)<[string, number, string?]>()(args),
    receiveFriendGift: (args: string) =>
        createAction(ActionTypes.RECEIVE_FRIENDGIFT)<string>()(args),
    receiveRewards: () => createAction(ActionTypes.RECEIVE_REWARDS)()(),
    recordGift: (args: [string, number, string?]) =>
        createAction(ActionTypes.RECORD_GIFT)<[string, number, string?]>()(args),
    recordFriendGift: () => createAction(ActionTypes.RECORD_FRIENDGIFT)()(),
    receiveMessage: (id: string) => createAction(ActionTypes.RECEIVE_MESSAGE)<string>()(id),
    registerFound: (ids: string[]) => createAction(ActionTypes.REGISTER_FOUND)<string[]>()(ids),
    registerMove: (ids: string[]) => createAction(ActionTypes.REGISTER_MOVE)<string[]>()(ids),
    removeBoosters: (ids: BoosterId[]) =>
        createAction(ActionTypes.REMOVE_GAME_BOOSTER)<BoosterId[]>()(ids),
    removeFriendBooster: (id: BoosterId) =>
        createAction(ActionTypes.REMOVE_FRIEND_BOOSTER)<BoosterId>()(id),
    removeMatch: (ids: string[][]) => createAction(ActionTypes.REMOVE_MATCH)<string[][]>()(ids),
    removeInvitation: (id: string) => createAction(ActionTypes.REMOVE_INVITATION)<string>()(id),
    removeStoredBooster: (id: BoosterId) =>
        createAction(ActionTypes.REMOVE_STORED_BOOSTER)<BoosterId>()(id),
    removeStoredPairs: (id: string) => createAction(ActionTypes.REMOVE_STORED)<string>()(id),
    removeTile: (id: string) => createAction(ActionTypes.REMOVE_TILE)<string>()(id),
    requestGift: (friendId: string) => createAction(ActionTypes.REQUEST_GIFT)<string>()(friendId),
    resetComboMeter: (willReset: boolean) =>
        createAction(ActionTypes.RESET_COMBO)<boolean>()(willReset),
    resetGiftCounter: () => createAction(ActionTypes.RESET_GIFT_COUNTER)()(),
    resetSendCoins: (friendId: string) =>
        createAction(ActionTypes.RESET_SEND_COINS)<string>()(friendId),
    resetSendGift: (friendId: string) =>
        createAction(ActionTypes.RESET_SEND_GIFT)<string>()(friendId),
    resetRequestGift: (friendId: string) =>
        createAction(ActionTypes.RESET_REQUEST_GIFT)<string>()(friendId),
    selectLevel: (id: string) => createAction(ActionTypes.SELECT_GAME_LEVEL)<string>()(id),
    selectTile: (id: string) => createAction(ActionTypes.SELECT_TILE)<string>()(id),
    seActiveCollection: (collection: Collection) =>
        createAction(ActionTypes.SET_ACTIVE_COLLECTION)<Collection>()(collection),
    setCollectablesProgress: (progress: [Collection, number]) =>
        createAction(ActionTypes.SET_COLLECTABLES_PROGRESS)<[Collection, number]>()(progress),
    setMultiplier: (m: number) => {
        if (Core.store.getState().StateLevel.timed) {
            return Actions.completeAction();
        }
        return createAction(ActionTypes.SET_MULTIPLIER)<number>()(m);
    },
    setTilesprites: () => createAction(ActionTypes.SET_TILESPRITES)()(),
    setPriceWheelCooldown: (ts: number) =>
        createAction(ActionTypes.SET_PRICE_WHEEL_COOLDOWN)<number>()(ts),
    setMissionRewardClaimed: (state: boolean[]) =>
        createAction(ActionTypes.SET_MISSION_REWARD_CLAIMED)<boolean[]>()(state),
    updateMissionCount: () => createAction(ActionTypes.UPDATE_MISSION_COUNT)()(),
    showBooster: (s: BoosterId) => createAction(ActionTypes.SHOW_BOOSTER)<BoosterId>()(s),
    shuffleTiles: () => createAction(ActionTypes.SHUFFLE_TILES)()(),
    sendCoins: (friendId: string) => createAction(ActionTypes.SEND_COINS)<string>()(friendId),
    sendGift: (friendId: string) => createAction(ActionTypes.SEND_GIFT)<string>()(friendId),
    shopGift: (n?: number) => {
        if (n !== null && n !== undefined) {
            return createAction(ActionTypes.SHOP_GIFT)<number>()(n);
        }
        return createAction(ActionTypes.SHOP_GIFT)<number>()(0);
    },
    shopOpened: () => createAction(ActionTypes.SHOP_OPENED)()(),
    silentStoreBooster: (ids: BoosterId[]) =>
        createAction(ActionTypes.SILENT_STORE_BOOSTER)<BoosterId[]>()(ids),
    startLevel: (id: string) => createAction(ActionTypes.START_LEVEL)<string>()(id),
    storeBooster: (ids: BoosterId[]) =>
        createAction(ActionTypes.STORE_GAME_BOOSTER)<BoosterId[]>()(ids),
    storeHints: (ids: string[]) => createAction(ActionTypes.STORE_HINTS)<string[]>()(ids),
    storeConfirmedInvites: (ids: string[]) =>
        createAction(ActionTypes.STORE_CONFIRMED_INVITE)<string[]>()(ids),
    storeReceivedInvites: (id: string) =>
        createAction(ActionTypes.STORE_RECEIVED_INVITE)<string>()(id),
    storeMatches: (ids: string[]) => createAction(ActionTypes.STORE_MATCHES)<string[]>()(ids),
    toggleMusic: (b: boolean) => createAction(ActionTypes.TOGGLE_MUSIC)<boolean>()(b),
    toggleSound: (b: boolean) => createAction(ActionTypes.TOGGLE_SOUND)<boolean>()(b),
    transitionMap: () => createAction(ActionTypes.TRANSITION_MAP)()(),
    tutorialAdvance: () => createAction(ActionTypes.TUTORIAL_ADVANCE)()(),
    tutorialEnd: (n: number) => createAction(ActionTypes.TUTORIAL_END)<number>()(n),
    tutorialStart: (n: number) => createAction(ActionTypes.TUTORIAL_START)<number>()(n),
    tutorialSetBooster: (booster: BoosterId) =>
        createAction(ActionTypes.TUTORIAL_SET_BOOSTER)<BoosterId>()(booster),
    tutorialUpdate: ({ actionId, payload }: ITutorialUpdatePayload) =>
        createAction(ActionTypes.TUTORIAL_UPDATE)<ITutorialUpdatePayload>()({ actionId, payload }),
    tutorialWait: () => createAction(ActionTypes.TUTORIAL_WAIT)()(),
    unlockBooster: (id: ItemIDs) => createAction(ActionTypes.UNLOCK_BOOSTER)<ItemIDs>()(id),
    unlockMap: (id: string) => createAction(ActionTypes.UNLOCK_MAP)<string>()(id),
    unlockMapLevel: (id: string) => createAction(ActionTypes.UNLOCK_MAP_LEVEL)<string>()(id),
    updateMapData: (args: IMapDataPayload) =>
        createAction(ActionTypes.UPDATE_MAP_DATA)<IMapDataPayload>()(args),
    updateHearts: (n: number) => createAction(ActionTypes.UPDATE_HEARTS)<number>()(n),
    updateCoins: (n: number) => createAction(ActionTypes.UPDATE_COINS)<number>()(n),
    visitMap: (s: string) => createAction(ActionTypes.VISIT_MAP)<string>()(s),
    setXPromoDismissed: (dismissed: boolean) =>
        createAction(ActionTypes.XPROMO_DISMISSED)<boolean>()(dismissed),
};

export type Actions = ActionsUnion<typeof Actions>;

// redux state actions
export const enum ActionTypes {
    ADD_CHEST = 'add chest',
    ADD_GIFT_TO_COUNTER = 'add gift to counter',
    ADD_FREE_BOOSTER = 'add free booster',
    BOOSTER_ACTION_COMPLETE = 'booster action complete',
    BLOCK_BOARD = 'block board',
    BOARD_READY = 'board ready',
    GAME_TRACKING = 'game tracking',
    CHANGE_VIEW = 'change view',
    CHANGE_CHEST_STATE = 'change chest state',
    START_LEVEL = 'start level',
    REMOVE_MATCH = 'remove match',
    FIND_OBJECT = 'find object',
    LOAD_LEVEL = 'load level',
    SHUFFLE_TILES = 'shuffle tiles',
    ADD_GAME_BOOSTER = 'add game booster',
    INCREMENT_BOOSTER_MATCH = 'incremet booster match counter',
    CANCEL_BOOSTER = 'cancel booster',
    REMOVE_GAME_BOOSTER = 'remove game booster',
    MATCH_TILES = 'match tiles',
    SELECT_TILE = 'select tile',
    REGISTER_MOVE = 'register move',
    DELETE_INVITE_CONFIRMED = 'delete invite confirmed',
    INVITE_CONFIRMED = 'invite_confirmed',
    INIT_CORE = 'init core',
    INTERSTITIAL_AD_WATCHED = 'interstitial ad watched',
    SELECT_GAME_LEVEL = 'select game level',
    CHANGE_MAP = 'change map',
    UNLOCK_MAP = 'unlock map',
    UNLOCK_MAP_LEVEL = 'unlock map level',
    UPDATE_MAP_DATA = 'update map data',
    ADD_LEVEL_SCORE = 'add level score',
    SET_LEVEL_SCORE = 'set level score',
    ADD_INVITATION = 'add invitation',
    SET_ACTIVE_COLLECTION = 'set active collection',
    SET_COLLECTABLES_PROGRESS = 'set collectables progress',
    SET_MULTIPLIER = 'set multiplier',
    SET_TILESPRITES = 'set tilesprites',
    COUNT_OBJECTS = 'count objects',
    REGISTER_FOUND = 'register found',
    LEVEL_COMPLETE = 'level complete',
    COMPLETE_LEVEL = 'complete level',
    LEVEL_STUCK = 'level stuck',
    ACTION_COMPLETE = 'action complete',
    SHOW_BOOSTER = 'show booster',
    ACTIVATE_BOOSTER = 'activate booster',
    ACTIVATE_FRIENDS_BOOSTERS = 'activate friends booster',
    ADD_FRIEND_BOOSTER = 'add friend booster',
    REMOVE_FRIEND_BOOSTER = 'remove friend booster',
    MISSION_COMPLETE = 'mission complete',
    MISSION_COUNT = 'mission count',
    MISSION_INIT = 'mission init',
    MISSION_REPLACE = 'mission replace',
    MISSION_REFRESH = 'mission refresh',
    MISSION_REFRESH_COMPLETE = 'mission refresh complete',
    MISSION_MILESTONE_REACHED = 'mission milestone reached',
    PAUSE_MENU_VISIBLE = 'pause menu visible',
    SET_MISSION_REWARD_CLAIMED = 'mission reward set claimed',
    UPDATE_MISSION_COUNT = 'update mission count',
    MISSIONS_SHOWN = 'missions shown',
    STORE_HINTS = 'store hints',
    STORE_MATCHES = 'store matches',
    REMOVE_INVITATION = 'remove invitation',
    REMOVE_STORED = 'remove stored',
    REMOVE_TILE = 'remove tile',
    RESET_COMBO = 'reset combo',
    OPT_IN_SHORTCUT_ACCEPTED = 'shortcut accepted',
    OPT_IN_SHORTCUT_DISMISSED = 'shortcut dismissed',
    OPT_IN_SHORTCUT_REWARDED = 'shortcut rewarded',
    OPT_IN_ACCEPTED = 'opt in accepted',
    OPT_IN_DISMISSED = 'opt in dismissed',
    OPT_IN_REWARDED = 'opt in rewarded',
    OPT_IN_PROMT = 'opt in promt',
    NATIVE_OPT_IN_PROMT = 'opt in native promt',
    NATIVE_OPT_IN_SHORTCUT_PROMT = 'opt in shortcut native promt',
    OPT_IN_SHOWN = 'opt in shown',
    OPT_IN_SHORTCUT_SHOWN = 'opt in shortcut shown',
    SAVE_PURCHASE = 'save purchase',
    POPUP_SHOWN = 'popup shown',
    POPUP_DISMISSED = 'popup dismissed',
    UPDATE_HEARTS = 'update hearts',
    UPDATE_COINS = 'update coins',
    SEND_COINS = 'send coins',
    SEND_GIFT = 'send gift',
    SET_PRICE_WHEEL_COOLDOWN = 'set price wheel cooldown',
    SHOP_OPENED = 'shop opened',
    SILENT_STORE_BOOSTER = 'silent store booster',
    STORE_GAME_BOOSTER = 'store game booster',
    STORE_CONFIRMED_INVITE = 'store confirmed invite',
    STORE_RECEIVED_INVITE = 'store received invite',
    RECEIVE_GIFT = 'receive gift',
    RECEIVE_FRIENDGIFT = 'receive friend gift',
    RECEIVE_MESSAGE = 'receive message',
    RECEIVE_REWARDS = 'receive rewards',
    RECORD_GIFT = 'record gift',
    RECORD_FRIENDGIFT = 'record friend gift',
    REMOVE_STORED_BOOSTER = 'remove stored booster',
    REQUEST_GIFT = 'request gift',
    RESET_GIFT_COUNTER = 'reset gift counter',
    RESET_SEND_COINS = 'reset send coins',
    RESET_SEND_GIFT = 'reset send gift',
    RESET_REQUEST_GIFT = 'reset request gift',
    TOGGLE_MUSIC = 'toggle music',
    TOGGLE_SOUND = 'toggle sound',
    TUTORIAL_START = 'tutorial start',
    TUTORIAL_END = 'tutorial end',
    TUTORIAL_UPDATE = 'tutorial update',
    TUTORIAL_SET_BOOSTER = 'set booster',
    TUTORIAL_WAIT = 'tutorial wait',
    TUTORIAL_ADVANCE = 'tutorial advance',
    TRANSITION_MAP = 'transition map',
    UNLOCK_BOOSTER = 'unlock booster',
    INIT_LEVEL = 'init level',
    CLEAR_PRELEVEL = 'clear prelevel',
    VISIT_MAP = 'visit map',
    SET_WIN_TIME = 'set win time',
    ADD_WIN_TIME = 'add win time',
    ASK_FRIEND_START = 'ask friend',
    ASK_FRIEND_COMPLETE = 'ask friend complete',
    SHOP_GIFT = 'shop gift',
    XPROMO_DISMISSED = 'xpromo dismissed',
}

// pixi container emit action constants
export const enum PopupActions {
    FRIENDJOINED_POPUP_CLOSE = 'friend joined popup close',
    GIFT_RECEIVED_POPUP_CLOSE = 'gift received popup close',
    ADERROR_CLOSE = 'ad error close',
    LOOSE_CLOSE = 'loose close',
    LOOSE_COMPLETE = 'loose complete',
    STUCK_CLOSE = 'shuffle close',
    STUCK_COMPLETE = 'shuffle complete',
    WIN_COMPLETE = 'win complete',
    WIN_CLOSE = 'win close',
    FINAL_REWARD = 'final-reward',
}

export const enum GameContainerAction {
    MAP_ACTION = 'map action',
    GAME_POPUP_ACTION = 'game popup action',
    TILE_SELECTED = 'tile selected',
    GAME_PRELEVEL_BOOSTER = 'game booster',
    GAME_ADD_CURRENCY = 'game add currency',
    GAME_PAUSE = 'game pause',
    GAME_UNPAUSE = 'game unpause',
    RECEIVE_REWARDS = 'receive rewards',
    TOGGLE_MUSIC = 'toggle music',
    TOGGLE_SOUND = 'toggle sound',
    QUIT_GAME = 'quit game',
    CURRENCY_CONFIRM_ACTION = 'currency confirm action',
    CURRENCY_CANCEL_ACTION = 'currency cancel action',
    DOUBLE_CHECK_CONFIRM_ACTION = 'double check confirm action',
    DOUBLE_CHECK_CANCEL_ACTION = 'double check cancel action',
    CONFIRM_ACTION = 'confirm action',
    CANCEL_ACTION = 'cancel action',
    REQUEST_AD = 'request ad',
}
